import { Box, Button, DialogActions, Grid, Typography } from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'
import Fraction from 'fraction.js'
import { useState } from 'react'
import {
  BooleanInput,
  DateInput,
  maxLength,
  minLength,
  NumberInput,
  ReferenceArrayInput,
  required,
  SelectArrayInput,
  SelectInput,
  TextInput,
  ValidationErrorMessage,
} from 'react-admin'
import { Form } from 'react-final-form'
import { formatReadableDate } from 'shared/helpers/dateHelpers'
import { decimalToFraction } from 'shared/helpers/formatters'
import { fractionToDecimal } from 'shared/helpers/utils'
import { EquipmentColor, EquipmentColorLabel } from 'shared/models/equipment'
import { bluefa, dividerGray } from 'shared/theme/colors'
import styled from 'styled-components'

import { captureExceptionIfProduction } from 'helpers/utils'
import { getVinInfo } from '../../api/core'
import {
  ELDType,
  ELDTypeChoices,
  EquipmentType,
  OwnershipTypeChoices,
  TRACTOR_CHOICES,
  TrailerSourceChoices,
  TrailerTypeChoices,
  TruckSourceChoices,
} from '../../helpers/constants'
import { getStateChoices, US_STATES } from '../../helpers/states'
import { getUserFullName } from '../../helpers/utils'
import { validateNoCharsOrSpecialChars, validateVinCharacters } from '../../helpers/validators'
import { ConditionalInput, SimpleNumberInput } from '../Fields'

const stateChoices = getStateChoices(US_STATES)

const yearChoices = () => {
  let currentYear = new Date().getFullYear()
  var years = []
  for (let i = currentYear + 4; i >= 1965; i--) {
    years.push({ id: i, name: i })
  }
  return years
}

export const EquipmentForm = ({ isCreate, equipmentType, initialValues, ...props }: any) => {
  const [vinFetchingError, setVinFetchingError] = useState<ValidationErrorMessage | undefined>(undefined)
  const [vinVersion, setVinVersion] = useState(0)
  const [make, setMake] = useState(initialValues?.vehicle_make || null)
  const [model, setModel] = useState(initialValues?.vehicle_model || null)
  const [year, setYear] = useState(initialValues?.vehicle_year || null)

  const handleKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      fetchVinInfo(event.target.value).then(event.target.blur())
    }
  }

  const fetchVinInfo = async (vinNumber: string) => {
    if (vinNumber && vinNumber.length === 17) {
      await getVinInfo(vinNumber, equipmentType)
        .then((vinInfo) => {
          setMake(vinInfo['make'])
          setModel(vinInfo['model'])
          setYear(vinInfo['year'])
          setVinFetchingError('')
          setVinVersion(vinVersion + 1)
        })
        .catch((error) => {
          if (error?.response && error?.response?.status === 404) {
            setVinFetchingError('Enter a valid 17-digit VIN')
            setVinVersion(vinVersion + 1)
          } else {
            captureExceptionIfProduction(error)
          }
        })
    }
  }

  return (
    <Box display="flex" flexDirection="column">
      <TextInput
        key={`${equipmentType}-vin-number-${vinFetchingError}`}
        source="vin_number"
        helperText={vinFetchingError === undefined ? '↩️ enter to to look up' : vinFetchingError ? '' : '✅'}
        onKeyPress={handleKeyPress}
        validate={[required(), minLength(17), maxLength(17), validateVinCharacters, () => vinFetchingError]}
        format={(v: any) => v && v.toUpperCase()}
        parse={(v: any) => (v ? v.toUpperCase() : '')}
        onChange={() => vinFetchingError !== undefined && setVinFetchingError(undefined)}
      />
      <TextInput
        source="vehicle_make"
        initialValue={make}
        validate={equipmentType === EquipmentType.Truck ? required() : undefined}
      />
      <TextInput
        source="vehicle_model"
        initialValue={model}
        validate={equipmentType === EquipmentType.Truck ? required() : undefined}
      />
      <SelectInput
        source="vehicle_year"
        initialValue={year}
        validate={equipmentType === EquipmentType.Truck ? required() : undefined}
        choices={yearChoices()}
      />
      <TextInput source="vehicle_number" />
      <SelectInput source="ownership_type" choices={OwnershipTypeChoices} validate={required()} />
      <SelectInput
        source="source"
        choices={equipmentType === EquipmentType.Truck ? TruckSourceChoices : TrailerSourceChoices}
      />
      <TextInput source="license_plate_number" validate={maxLength(8)} />
      <SelectInput source="license_plate_state" choices={stateChoices} />
      <TextInput source="internal_notes" component="pre" multiline />
      {props.children}
      {isCreate && equipmentType === EquipmentType.Truck && (
        <>
          <DateInput source="insurance_period_date_start" />
          <DateInput source="insurance_period_date_end" />
          <TextInput source="insurance_period_date_end_reason" />
        </>
      )}
    </Box>
  )
}

export const TruckForm = ({ teamId, isCreate, ...props }: any) => {
  const { hasTruckSyncToMotive } = props
  return (
    <EquipmentForm isCreate={isCreate} equipmentType={EquipmentType.Truck} {...props}>
      <SelectInput
        source="vehicle_color"
        choices={Object.keys(EquipmentColor).map((color) => ({
          id: color,
          name: EquipmentColorLabel[color as EquipmentColor],
        }))}
      />
      <SelectInput source="eld_type" choices={ELDTypeChoices} initialValue={ELDType.KeepTruckin} disabled />
      <TextInput
        source="eld_serial_number"
        parse={(value: String | undefined | null) => (value ? value : '')}
        label="Vehicle gateway"
      />
      <ReferenceArrayInput
        source="assigned_user_ids"
        reference="users"
        filter={{ is_active: true, team: teamId, is_team_driver: true }}
        fullWidth
        label="Assigned Drivers"
      >
        <SelectArrayInput
          optionText={(driver: any) => {
            return getUserFullName(driver) || ''
          }}
        />
      </ReferenceArrayInput>
      <SelectInput source="type" choices={TRACTOR_CHOICES} />
      <NumberInput source="weight_lb" />
      <BooleanInput source="plates_program_required" label="CT gets plates" />
      <ConditionalInput when="plates_program_required" is={true}>
        <DateInput source="date_acquired" />
        <NumberInput source="odometer_reading_mi" />
        <NumberInput source="price_dollars" />
      </ConditionalInput>
      <BooleanInput source="ifta_required" label="CT manages IFTA" />
      <ConditionalInput when="ifta_required" is={true}>
        <TextInput source="ifta_sticker_number" validate={maxLength(12)} />
      </ConditionalInput>
      {hasTruckSyncToMotive && (
        <ConditionalInput when="eld_serial_number" isNotEmpty={true}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            spacing={2}
            style={{ backgroundColor: bluefa }}
          >
            <Grid item xs={1} justifyContent="flex-end" container>
              <InfoIcon color="primary" fontSize="small" style={{ marginTop: '5px' }} />
            </Grid>
            <Grid item xs={11}>
              <Typography variant="body1" align="left">
                Adding a new ELD serial number will sync the following information of this truck to Motive:
                <li>ELD serial number</li>
                <li>Vin</li>
                <li>Year</li>
                <li>Make</li>
                <li>Model</li>
                <li>License plate state</li>
                <li>License plate number</li>
                <li>CT manages IFTA</li>
              </Typography>
            </Grid>
          </Grid>
        </ConditionalInput>
      )}
    </EquipmentForm>
  )
}

export const TrailerForm = ({ isCreate, ...props }: any) => (
  <EquipmentForm isCreate={isCreate} equipmentType={EquipmentType.Trailer} {...props}>
    <SelectInput source="type" choices={TrailerTypeChoices} />
    <NumberInput source="length_ft" />
  </EquipmentForm>
)

export const FractionInput = ({ source, label }: any) => {
  return (
    <TextInput
      source={source}
      label={label}
      format={decimalToFraction}
      parse={fractionToDecimal}
      validate={[validateNoCharsOrSpecialChars]}
      helperText="inches; ie 1 1/2"
    />
  )
}

const decimalToNumeratorThirtySeconds = (value: any) => {
  if (!value || value === 'NaN') {
    return
  }

  const fraction = new Fraction(value).simplify(0.00000001)

  // convert the fraction to 32nds
  if (fraction.d === 32) return fraction.n

  const multiplier = 32 / fraction.d
  return fraction.n * multiplier
}

export const ThirtySecondFractionInput = ({ source, label }: any) => {
  return (
    <SimpleNumberInput
      source={source}
      format={decimalToNumeratorThirtySeconds}
      parse={(v: any) => (v ? v / 32 : 0)}
      label={label}
      helperText="32nds of an inch"
    />
  )
}

export const EquipmentInspectionReportForm = ({
  initialValues,
  handleSubmit,
  basePath,
  redirectTo,
  isTruck,
}: any) => (
  <Form
    display="flex"
    flexDirection="column"
    onSubmit={handleSubmit}
    initialValues={initialValues}
    keepDirtyOnReinitialize
    render={(formProps) => {
      let inspectionDateString = '-'
      if (formProps.initialValues && formProps.initialValues.inspection_report_expiration_date) {
        inspectionDateString = formatReadableDate(formProps.initialValues.inspection_report_expiration_date)
      }
      return (
        <form onSubmit={formProps.handleSubmit}>
          <Box display="flex" flexDirection="column" width={700} minWidth={300} m={2}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {'Inspection: ' + inspectionDateString}
              <HorizontalLine />
            </Typography>
            {isTruck && (
              <>
                <Typography variant="h6" style={{ marginBottom: '60px', fontWeight: 600 }}>
                  {'Steer'}
                </Typography>
                <Grid container justifyContent="center" direction="row" item spacing={0}>
                  <Grid
                    item
                    xs={1}
                    alignItems="center"
                    container
                    direction="row"
                    style={{ marginRight: '40px' }}
                  >
                    <Typography variant="h3">{'L'}</Typography>
                  </Grid>
                  <Grid container justifyContent="center" direction="column" item xs={8} spacing={2}>
                    <Grid container justifyContent="center" direction="row" spacing={1}>
                      <Grid item xs={3} style={{ marginLeft: '-20px' }}>
                        <ThirtySecondFractionInput source="tread_depth_steer_left" label="Tread" />
                      </Grid>
                      <Grid item xs={3} style={{ marginRight: '20px' }}>
                        <FractionInput source="brake_stroke_steer_left" label="Stroke" />
                      </Grid>
                      <Grid item xs={3}>
                        <FractionInput source="brake_stroke_steer_right" label="Stroke" />
                      </Grid>
                      <Grid item xs={3}>
                        <ThirtySecondFractionInput source="tread_depth_steer_right" label="Tread" />
                      </Grid>
                    </Grid>
                    <Grid container justifyContent="center" direction="row" spacing={1}>
                      <Grid item xs={3} style={{ marginLeft: '-20px' }}>
                        <SimpleNumberInput source="tire_pressure_steer_left" label="PSI" />
                      </Grid>
                      <Grid item xs={3} style={{ marginRight: '20px' }}>
                        <FractionInput source="brake_liner_steer_left" label="Brake liner" />
                      </Grid>
                      <Grid item xs={3}>
                        <FractionInput source="brake_liner_steer_right" label="Brake liner" />
                      </Grid>
                      <Grid item xs={3}>
                        <SimpleNumberInput source="tire_pressure_steer_right" label="PSI" min={0} />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    alignItems="center"
                    justifyContent="flex-end"
                    container
                    direction="row"
                    style={{ marginLeft: '30px' }}
                  >
                    <Typography variant="h3">{'R'}</Typography>
                  </Grid>
                </Grid>
                <HorizontalLine />
              </>
            )}
            <Typography variant="h6" style={{ marginBottom: '30px', fontWeight: 600 }}>
              {'Front Drive'}
            </Typography>
            <Box>
              <Grid
                container
                justifyContent="center"
                direction="row"
                spacing={2}
                style={{ marginLeft: '0px' }}
              >
                <Grid item xs={2} style={{ marginLeft: '-10px' }}>
                  <ThirtySecondFractionInput source="tread_depth_axle_1_left_outer" label="Outer tread" />
                </Grid>
                <Grid item xs={2}>
                  <ThirtySecondFractionInput source="tread_depth_axle_1_left_inner" label="Inner tread" />
                </Grid>
                <Grid item xs={2}>
                  <FractionInput source="brake_stroke_axle_1_left" label="Stroke" />
                </Grid>
                <Grid item xs={2} style={{ marginLeft: '10px' }}>
                  <FractionInput source="brake_stroke_axle_1_right" label="Stroke" />
                </Grid>
                <Grid item xs={2}>
                  <ThirtySecondFractionInput source="tread_depth_axle_1_right_inner" label="Inner tread" />
                </Grid>
                <Grid item xs={2}>
                  <ThirtySecondFractionInput source="tread_depth_axle_1_right_outer" label="Outer tread" />
                </Grid>
                <Grid item xs={2} style={{ marginLeft: '-10px' }}>
                  <SimpleNumberInput source="tire_pressure_axle_1_left_outer" label="PSI" min={0} />
                </Grid>
                <Grid item xs={2}>
                  <SimpleNumberInput source="tire_pressure_axle_1_left_inner" label="PSI" min={0} />
                </Grid>
                <Grid item xs={2}>
                  <FractionInput source="brake_liner_axle_1_left" label="Brake liner" />
                </Grid>
                <Grid item xs={2} style={{ marginLeft: '10px' }}>
                  <FractionInput source="brake_liner_axle_1_right" label="Brake liner" />
                </Grid>
                <Grid item xs={2}>
                  <SimpleNumberInput source="tire_pressure_axle_1_right_inner" label="PSI" min={0} />
                </Grid>
                <Grid item xs={2}>
                  <SimpleNumberInput source="tire_pressure_axle_1_right_outer" label="PSI" min={0} />
                </Grid>
              </Grid>
            </Box>
            <HorizontalLine />
            <Typography variant="h6" style={{ marginBottom: '30px', fontWeight: 600 }}>
              {'Rear Drive'}
            </Typography>
            <Box>
              <Grid
                container
                justifyContent="center"
                direction="row"
                spacing={2}
                style={{ marginLeft: '0px' }}
              >
                <Grid item xs={2} style={{ marginLeft: '-10px' }}>
                  <ThirtySecondFractionInput source="tread_depth_axle_2_left_outer" label="Outer tread" />
                </Grid>
                <Grid item xs={2}>
                  <ThirtySecondFractionInput source="tread_depth_axle_2_left_inner" label="Inner tread" />
                </Grid>
                <Grid item xs={2}>
                  <FractionInput source="brake_stroke_axle_2_left" label="Stroke" />
                </Grid>
                <Grid item xs={2} style={{ marginLeft: '10px' }}>
                  <FractionInput source="brake_stroke_axle_2_right" label="Stroke" />
                </Grid>
                <Grid item xs={2}>
                  <ThirtySecondFractionInput source="tread_depth_axle_2_right_inner" label="Inner tread" />
                </Grid>
                <Grid item xs={2}>
                  <ThirtySecondFractionInput source="tread_depth_axle_2_right_outer" label="Outer tread" />
                </Grid>
                <Grid item xs={2} style={{ marginLeft: '-10px' }}>
                  <SimpleNumberInput source="tire_pressure_axle_2_left_outer" label="PSI" min={0} />
                </Grid>
                <Grid item xs={2}>
                  <SimpleNumberInput source="tire_pressure_axle_2_left_inner" label="PSI" min={0} />
                </Grid>
                <Grid item xs={2}>
                  <FractionInput source="brake_liner_axle_2_left" label="Brake liner" />
                </Grid>
                <Grid item xs={2} style={{ marginLeft: '10px' }}>
                  <FractionInput source="brake_liner_axle_2_right" label="Brake liner" />
                </Grid>
                <Grid item xs={2}>
                  <SimpleNumberInput source="tire_pressure_axle_2_right_inner" label="PSI" min={0} />
                </Grid>
                <Grid item xs={2}>
                  <SimpleNumberInput source="tire_pressure_axle_2_right_outer" label="PSI" min={0} />
                </Grid>
              </Grid>
            </Box>
            <HorizontalLine />
            <StyledTextInput source="additional_info" label="Additional notes" />
          </Box>
          <DialogActions style={{ justifyContent: 'left' }}>
            <Button variant="text" onClick={() => redirectTo(basePath)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={formProps.handleSubmit} color="primary" type="submit">
              Save
            </Button>
          </DialogActions>
        </form>
      )
    }}
  />
)

const StyledTextInput = styled(TextInput)`
  & > div {
    min-height: 100px;
  }
`

const HorizontalLine = styled.div`
  border-style: solid;
  border-top-width: 1px;
  border-bottom-width: 0;
  border-color: ${dividerGray};
  position: relative;
  margin-top: 20px;
  margin-bottom: 20px;
`
